import React from 'react';
import { ValidatorData } from '../../types/api';

interface ValidatorCardProps {
  validator: ValidatorData;
  status: 'online' | 'offline';
}

const ValidatorCard: React.FC<ValidatorCardProps> = ({ validator }) => {
  const getStatusColor = (isOnline: boolean) => {
    return isOnline ? 'bg-green-500' : 'bg-red-500';
  };

  const getSuccessRateColor = (rate: number) => {
    if (rate >= 95) return 'text-green-600';
    if (rate >= 90) return 'text-yellow-600';
    return 'text-red-600';
  };

  const getResponseTimeColor = (responseTime: number) => {
    if (responseTime < 1000) return 'text-green-600';
    if (responseTime < 3000) return 'text-yellow-600';
    return 'text-red-600';
  };

  const formatResponseTime = (ms: number) => {
    if (ms < 1000) return `${ms.toFixed(0)}ms`;
    return `${(ms / 1000).toFixed(1)}s`;
  };

  const getTimeAgo = (timestamp: number) => {
    const diff = Date.now() - timestamp; // timestamp is already in milliseconds from health monitor
    const minutes = Math.floor(diff / (1000 * 60));
    const hours = Math.floor(minutes / 60);
    const days = Math.floor(hours / 24);
    
    if (days > 0) return `${days}d ago`;
    if (hours > 0) return `${hours}h ago`;
    if (minutes > 0) return `${minutes}m ago`;
    return 'Just now';
  };

  const truncateAddress = (address: string) => {
    return `${address.slice(0, 6)}...${address.slice(-4)}`;
  };

  return (
    <div className={`bg-white rounded-lg shadow p-6 border-l-4 ${
      validator.isOnline ? 'border-green-500' : 'border-red-500'
    }`}>
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center space-x-3">
          <div className={`w-3 h-3 rounded-full ${getStatusColor(validator.isOnline)}`}></div>
          <h3 className="text-lg font-medium text-gray-900">
            {validator.name || `Validator ${validator.validator.slice(0, 8)}`}
          </h3>
        </div>
        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
          validator.isOnline 
            ? 'bg-green-100 text-green-800' 
            : 'bg-red-100 text-red-800'
        }`}>
          {validator.isOnline ? 'Online' : 'Offline'}
        </span>
      </div>

      {/* Validator Address */}
      <div className="mb-4">
        <p className="text-sm text-gray-500">Address</p>
        <p className="text-sm font-mono text-gray-900" title={validator.validator}>
          {truncateAddress(validator.validator)}
        </p>
      </div>

      {/* Performance Metrics */}
      <div className="space-y-3">
        
        {/* Response Time */}
        <div>
          <div className="flex justify-between items-center">
            <span className="text-sm text-gray-500">Response Time</span>
            <span className={`text-sm font-semibold ${getResponseTimeColor(validator.responseTime)}`}>
              {formatResponseTime(validator.responseTime)}
            </span>
          </div>
        </div>

        {/* Success Rate */}
        <div>
          <div className="flex justify-between items-center">
            <span className="text-sm text-gray-500">Success Rate</span>
            <span className={`text-sm font-semibold ${getSuccessRateColor(validator.successRate || 0)}`}>
              {validator.successRate ? validator.successRate.toFixed(1) : 'N/A'}%
            </span>
          </div>
        </div>

        {/* Total Approvals */}
        <div>
          <div className="flex justify-between items-center">
            <span className="text-sm text-gray-500">Total Approvals</span>
            <span className="text-sm font-semibold text-gray-900">
              {(validator.totalApprovals || 0).toLocaleString()}
            </span>
          </div>
        </div>

        {/* Last Active */}
        <div>
          <div className="flex justify-between items-center">
            <span className="text-sm text-gray-500">Last Checked</span>
            <span className="text-sm text-gray-700">
              {getTimeAgo(validator.lastActiveTime)}
            </span>
          </div>
        </div>

        {/* Health-specific information */}
        {(validator as any).consecutiveFailures !== undefined && (validator as any).consecutiveFailures > 0 && (
          <div>
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-500">Consecutive Failures</span>
              <span className="text-sm font-semibold text-red-600">
                {(validator as any).consecutiveFailures}
              </span>
            </div>
          </div>
        )}

        {(validator as any).errorMessage && (
          <div>
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-500">Last Error</span>
              <span className="text-xs text-red-600 truncate max-w-32" title={(validator as any).errorMessage}>
                {(validator as any).errorMessage}
              </span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ValidatorCard; 